import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { useContent } from '@/content'
import { ROUTES } from '@/lib/paths'
import { Icon, Surface } from '@/components/ui'
import styles from './DomainExpertise.module.css'

/**
 * The three domains, each expanded into the services it contains and the
 * project that proves it.
 *
 * There is one taxonomy on this site. Services are not a second list
 * competing with domains — each one belongs to a domain and is shown inside
 * it, which is why the old duplicate service grid on the about page is gone.
 */
export const DomainExpertise = () => {
  const { domains, services, projects } = useContent()

  return (
    <div className={styles.stack}>
      {domains.map((domain) => {
        const owned = services.filter((service) => service.domain === domain.id)
        const flagship = projects.find(
          (project) => project.slug === owned[0]?.projectSlug,
        )

        return (
          <Surface key={domain.id} pad={25} radius="shell" className={styles.panel}>
            <header className={styles.head}>
              <span className={styles.icon} aria-hidden="true">
                <Icon name={domain.icon} size={24} />
              </span>
              <div>
                <h2 className="t-24">{domain.name}</h2>
                <p className={`t-14 ${styles.lede}`}>{domain.description}</p>
              </div>
            </header>

            <ul className={styles.services}>
              {owned.map((service, index) => (
                <motion.li
                  key={service.slug}
                  id={service.slug}
                  initial={{ opacity: 0, transform: 'translateY(14px)' }}
                  whileInView={{ opacity: 1, transform: 'translateY(0px)' }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{
                    duration: 0.45,
                    delay: index * 0.06,
                    ease: [0.23, 1, 0.32, 1],
                  }}
                >
                  <Surface interactive radius="card" className={styles.service}>
                    <h3 className="t-18">{service.name}</h3>
                    <p className={`t-14 ${styles.description}`}>
                      {service.description}
                    </p>
                    <ul className={styles.deliverables}>
                      {service.deliverables.map((item) => (
                        <li key={item} className={`t-12 ${styles.deliverable}`}>
                          <Icon name="arrowRight" size={12} className={styles.bullet} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </Surface>
                </motion.li>
              ))}
            </ul>

            <footer className={styles.footer}>
              <Link
                to={`${ROUTES.projects}?domain=${domain.id}`}
                className={styles.link}
              >
                All {domain.name.toLowerCase()} work
                <Icon name="arrowUpRight" size={12} />
              </Link>
              {flagship && (
                <Link to={ROUTES.project(flagship.slug)} className={styles.link}>
                  Flagship: {flagship.title}
                  <Icon name="arrowUpRight" size={12} />
                </Link>
              )}
            </footer>
          </Surface>
        )
      })}
    </div>
  )
}
